import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money } from "@/dealership/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DeskDealButton from "@/dealership/components/desk/DeskDealButton";
import { ArrowLeft, Pencil, Trash2, Car, Wrench } from "lucide-react";

const STATUSES = ["In Stock", "Available", "Reconditioning", "Pending", "Sold"];
const statusStyle = {
  "In Stock": "bg-blue-500/12 text-blue-400", Available: "bg-green-500/12 text-green-400",
  Reconditioning: "bg-amber-500/12 text-amber-400", Pending: "bg-purple-500/12 text-purple-400",
  Sold: "bg-zinc-500/20 text-zinc-400",
};

function Row({ label, value, bold, accent }) {
  if (value === "" || value == null) return null;
  return (
    <div className={`flex justify-between py-2 border-b border-border last:border-0 text-sm ${bold ? "font-bold" : ""}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className={accent ? "text-primary font-bold" : ""}>{value}</span>
    </div>
  );
}

export default function InventoryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: v, isLoading, isError } = useQuery({
    queryKey: ["vehicle", id],
    queryFn: async () => (await api.get(`/inventory/${id}`)).data,
  });

  const statusMut = useMutation({
    mutationFn: async (status) => (await api.put(`/inventory/${id}`, { ...v, status })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vehicle", id] }); qc.invalidateQueries({ queryKey: ["inventory"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Status updated"); },
    onError: () => toast.error("Failed to update status"),
  });
  const delMut = useMutation({
    mutationFn: async () => api.delete(`/inventory/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["inventory"] }); toast.success("Vehicle deleted"); navigate("/dealership/inventory"); },
  });

  if (isLoading) return <div className="p-8 text-muted-foreground">Loading…</div>;
  if (isError || !v) return <div className="p-8 text-muted-foreground">Vehicle not found. <button className="text-primary underline" onClick={() => navigate("/dealership/inventory")}>Back to Inventory</button></div>;

  const recon = Number(v.recon_cost ?? (v.recon_items || []).reduce((s, i) => s + Number(i.cost || 0), 0));
  const mechItems = v.mechanic_items || [];
  const potential = Number(v.listing_price || 0) - Number(v.total_cost || 0);

  return (
    <div className="p-8 max-w-5xl" data-testid="vehicle-detail-page">
      <button onClick={() => navigate("/dealership/inventory")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="vehicle-detail-back">
        <ArrowLeft className="h-4 w-4" /> Back to Inventory
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl">{v.year} {v.make} {v.model}</h1>
            <Badge className={`${statusStyle[v.status]} border-0`} data-testid="vehicle-detail-status-badge">{v.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1 font-mono-data">{v.stock_number} · VIN {v.vin}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={v.status} onValueChange={(s) => statusMut.mutate(s)}>
            <SelectTrigger className="w-44" data-testid="vehicle-detail-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <DeskDealButton vehicleId={id} label="Desk a deal" testid="vehicle-detail-desk" />
          <Button variant="outline" onClick={() => navigate(`/dealership/inventory?edit=${id}`)} data-testid="vehicle-detail-edit"><Pencil className="h-4 w-4 mr-2" />Edit</Button>
          <Button variant="outline" className="text-destructive hover:text-destructive" onClick={() => { if (window.confirm("Delete this vehicle?")) delMut.mutate(); }} data-testid="vehicle-detail-delete"><Trash2 className="h-4 w-4 mr-2" />Delete</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3"><Car className="h-4 w-4 text-muted-foreground" /><p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Vehicle</p></div>
          <Row label="Year / Make / Model" value={`${v.year} ${v.make} ${v.model}`} />
          <Row label="Trim" value={v.trim} />
          <Row label="Color" value={v.color} />
          <Row label="Body Type" value={v.body_type} />
          <Row label="Odometer" value={v.odometer ? `${Number(v.odometer).toLocaleString()} km` : ""} />
          <Row label="VIN" value={v.vin} />
          <Row label="Stock #" value={v.stock_number} />
          <Row label="Source" value={v.source} />
          <Row label="Purchase Date" value={v.purchase_date} />
          {v.notes ? <Row label="Notes" value={v.notes} /> : null}
        </Card>

        <Card className="p-5">
          <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-3">Cost & Profit</p>
          <Row label="Purchase Cost" value={money(v.purchase_cost)} />
          {v.auction_fee ? <Row label="Auction Fee" value={money(v.auction_fee)} /> : null}
          {v.transport_fee ? <Row label="Transport Fee" value={money(v.transport_fee)} /> : null}
          {recon ? <Row label="Reconditioning" value={money(recon)} /> : null}
          {v.other_costs ? <Row label="Other Costs" value={money(v.other_costs)} /> : null}
          {v.tax_included && v.tax ? <Row label="Purchase HST (13%)" value={money(v.tax)} /> : null}
          <Row label="Total Landed Cost" value={money(v.total_cost)} bold />
          <Row label="Listing Price" value={money(v.listing_price)} />
          <Row label="Potential Profit" value={money(potential)} accent />

          {(v.recon_items || []).length > 0 && (
            <div className="mt-4">
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-2">Reconditioning Items</p>
              {v.recon_items.map((r, i) => (
                <div key={i} className="flex justify-between text-sm py-1"><span className="text-muted-foreground">{r.description}</span><span>{money(r.cost)}</span></div>
              ))}
            </div>
          )}

          {mechItems.length > 0 && (
            <div className="mt-4" data-testid="vehicle-detail-mechanic-work">
              <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-2 flex items-center gap-1.5"><Wrench className="h-3 w-3" /> Mechanic Service Work</p>
              {mechItems.map((m, i) => (
                <div key={i} className="flex justify-between text-sm py-1" data-testid={`mechanic-work-item-${i}`}>
                  <span className="text-muted-foreground flex items-center gap-2">
                    {m.description}
                    {m.status ? <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-amber-500/35 text-amber-400">{m.status}</Badge> : null}
                  </span>
                  <span>{money(m.cost)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm py-1 mt-1 border-t border-border pt-2 font-semibold">
                <span className="text-muted-foreground">Service Total</span><span>{money(Number(v.mechanic_cost || 0))}</span>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
